import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Badge, Button, Field, Input, Spinner, Text, makeStyles, tokens } from '@fluentui/react-components';
import { fetchCalculators, runCalculator, type CalculatorInfo } from '../api/calculators.api';
import { useT } from '../i18n';
import { usePageTitle } from '../hooks/usePageTitle';
import { PageTitle } from '../components/shared/PageTitle';

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, padding: tokens.spacingVerticalL },
  list: { display: 'flex', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
  form: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, maxWidth: '480px' },
  result: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  row: { display: 'flex', gap: tokens.spacingHorizontalM },
});

/** Número si el texto lo es; si no, texto (p. ej. el modelo de cobro). */
function parseValue(raw: string): string | number {
  const n = Number(raw);
  return raw.trim() !== '' && Number.isFinite(n) ? n : raw.trim();
}

function show(v: unknown): string {
  return v !== null && typeof v === 'object' ? JSON.stringify(v) : String(v);
}

/** Calculadoras del catálogo: el formulario se genera desde `entradas`. */
export function CalculadorasPage() {
  const s = useStyles();
  const c = useT().calculators;
  usePageTitle(c.title);

  const [selected, setSelected] = useState<CalculatorInfo | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});

  const catalog = useQuery({ queryKey: ['calculators'], queryFn: fetchCalculators });
  const run = useMutation({
    mutationFn: (input: Record<string, unknown>) => runCalculator(selected!.id, input),
  });

  const choose = (calc: CalculatorInfo) => {
    setSelected(calc);
    setValues({});
    run.reset();
  };

  const submit = () => {
    const input: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(values)) if (v.trim() !== '') input[k] = parseValue(v);
    run.mutate(input);
  };

  return (
    <div className={s.root}>
      <PageTitle>{c.title}</PageTitle>
      <Text>{c.subtitle}</Text>
      {catalog.isLoading && <Spinner />}
      {catalog.isError && <Text>{c.loadError}</Text>}
      <div className={s.list}>
        {(catalog.data ?? []).map((calc) => (
          <Button key={calc.id} appearance={selected?.id === calc.id ? 'primary' : 'secondary'} onClick={() => choose(calc)}>
            {calc.titulo}
          </Button>
        ))}
      </div>
      {selected && (
        <form className={s.form} onSubmit={(e) => { e.preventDefault(); submit(); }}>
          {selected.entradas.map((e) => (
            <Field
              key={e.clave}
              label={`${e.etiqueta}${e.unidad ? ` (${e.unidad})` : ''}${e.obligatoria ? ' *' : ''}`}
            >
              <Input
                value={values[e.clave] ?? ''}
                onChange={(_, d) => setValues((p) => ({ ...p, [e.clave]: d.value }))}
                contentAfter={e.fuente === 'sistema' ? <Badge appearance="tint">{c.fromSystem}</Badge> : undefined}
              />
            </Field>
          ))}
          <Button appearance="primary" type="submit" disabled={run.isPending}>{c.calculate}</Button>
        </form>
      )}
      {run.isError && <Text>{c.runError}</Text>}
      {run.data && (
        <div className={s.result}>
          <Text weight="semibold">{c.result}</Text>
          {Object.entries(run.data).map(([k, v]) => (
            <div key={k} className={s.row}><Text>{k}</Text><Text>{show(v)}</Text></div>
          ))}
        </div>
      )}
    </div>
  );
}
